import {
  Classification,
  ClassificationParameterExtended,
  ClassificationClass,
  ClassificationType,
} from "interfaces/classification";
import { Style } from "../../../ducks/style/style";
import {
  isTwoDimensionalSymbol,
  StyleSymbolType,
  ParameterByAttributeValue,
  ParameterValue,
  isStyle,
  FillBrushType,
  StrokeBrushType,
  ParameterValueWithSymbol,
} from "../../../interfaces/style";
import { ClassificationCondition } from "../condition/ClassificationCondition";
import {
  isParameterByAttribute,
  isStrokeParameter,
  isFillParameter,
  isSvgFillParameter,
  isSvgStrokeParameter,
} from "./getFromClassified";

export interface ClassificationExtras {
  defaultTitle: string;
  defaultValue: ParameterValue;
}

export type ExtendedClassification<
  Type extends ClassificationType = ClassificationType,
  Value extends ClassificationClass = ClassificationClass,
> = Classification<Type, Value> & ClassificationExtras;

const getClassificationValue = (
  parameterValue: ParameterValueWithSymbol,
  title: string,
  condition: ClassificationCondition,
): ClassificationClass => ({
  parameterValue,
  title,
  ...(condition.isUnique()
    ? {
        uniqueValue: condition.uniqueValue,
      }
    : {
        from: condition.range[0],
        to: condition.range[1],
      }),
});

const DEFAULT_EXTRAS: ClassificationExtras = {
  defaultTitle: "",
  defaultValue: null,
};

export const getClassification = (
  values: (ParameterByAttributeValue<ParameterValue> | Style)[],
  parameter: ClassificationParameterExtended,
  extras: ClassificationExtras = DEFAULT_EXTRAS,
): ExtendedClassification =>
  values.reduce(
    (classification: ExtendedClassification, value) => {
      const classificationCondition = new ClassificationCondition(value.condition);
      const classificationValue = getClassificationValue(
        isStyle(value) ? value.symbol : value.value,
        value.title,
        classificationCondition,
      );

      classification.values.push(classificationValue);

      return {
        ...classification,
        type: classification.type || classificationCondition.type,
        attribute: classification.attribute || classificationCondition.attributeName,
      };
    },
    {
      ...extras,
      attribute: null,
      parameter,
      type: null,
      values: [],
    },
  );

const createClassificationParameter = (parameter: string, parentParameter?: string) =>
  [parentParameter, parameter].filter(Boolean).join(".") as ClassificationParameterExtended;

export const getClassifications = (
  symbolOrBrush: StyleSymbolType | FillBrushType | StrokeBrushType,
  parentParameter?: string,
): ExtendedClassification[] => {
  if (!symbolOrBrush) {
    return [];
  }

  return Object.entries(symbolOrBrush).reduce((classifications, [parameter, parameterValue]) => {
    if (!parameterValue) {
      return classifications;
    }

    if (isParameterByAttribute(parameterValue)) {
      // HACK: for remove duplicate legend sections (classificated by `height`, `width` will have the same legend sections)
      if (isTwoDimensionalSymbol(symbolOrBrush as StyleSymbolType) && parameter === "height") {
        return classifications;
      }
      classifications.push(
        getClassification(parameterValue.values, createClassificationParameter(parameter, parentParameter), {
          defaultTitle: parameterValue.title,
          defaultValue: parameterValue.defaultValue,
        }),
      );
    }

    if (
      isFillParameter(parameter, parameterValue) ||
      isStrokeParameter(parameter, parameterValue) ||
      isSvgFillParameter(parameter, parameterValue) ||
      isSvgStrokeParameter(parameter, parameterValue)
    ) {
      classifications.push(...getClassifications(parameterValue, parameter));
    }

    return classifications;
  }, []);
};
